import { Alert, AlertTitle, Typography } from '@mui/material'
import {
  HoldReaction,
  HoldRestrictionSummaryDto,
  OperationType,
  WorkInstructionWarningReason,
} from '@operations/app/api'
import { useTranslate } from '@tolgee/react'

interface Props {
  warningReason?: WorkInstructionWarningReason | null
  isOnStepper?: boolean
  entryHoldRestriction?: HoldRestrictionSummaryDto | null
  exitHoldRestriction?: HoldRestrictionSummaryDto | null
  yardHoldRestriction?: HoldRestrictionSummaryDto | null
  operationType?: OperationType
}

const getHoldWarningNames = (
  operationType: OperationType | undefined,
  entryHoldRestriction: HoldRestrictionSummaryDto | null | undefined,
  exitHoldRestriction: HoldRestrictionSummaryDto | null | undefined,
  yardHoldRestriction: HoldRestrictionSummaryDto | null | undefined,
): string[] => {
  const applicable: HoldRestrictionSummaryDto[] = []

  if (
    operationType === OperationType.Inbound &&
    entryHoldRestriction?.reaction === HoldReaction.Warning
  )
    applicable.push(entryHoldRestriction)
  if (
    operationType === OperationType.Outbound &&
    exitHoldRestriction?.reaction === HoldReaction.Warning
  )
    applicable.push(exitHoldRestriction)
  if (yardHoldRestriction?.reaction === HoldReaction.Warning) applicable.push(yardHoldRestriction)

  return [...new Set(applicable.flatMap(r => r.holdTypeNames))]
}

export const JobAlert = ({
  warningReason,
  isOnStepper,
  entryHoldRestriction,
  exitHoldRestriction,
  yardHoldRestriction,
  operationType,
}: Props) => {
  const { t } = useTranslate()
  const nonAlertableWarningReasons: WorkInstructionWarningReason[] = [
    WorkInstructionWarningReason.LinkedInboundForceFinishedByGateOutWithoutLocation,
  ]

  const getTitle = () => {
    switch (warningReason) {
      case WorkInstructionWarningReason.CarrierNotReadyForOperation:
        return t('theCarrierIsNotReadyForOperation', 'The carrier is not ready for operation')
      case WorkInstructionWarningReason.LinkedInboundNotFinished:
        return t('theDischargeIsNotCompleted', 'The discharge is not completed')
      case WorkInstructionWarningReason.LinkedInboundForceFinishedByGateOutWithoutLocation:
      case WorkInstructionWarningReason.OnYardWithoutPosition:
        return t('weAreUnableToFindaTheLocation', 'We are unable to find the location')
      case WorkInstructionWarningReason.RequiresReservation:
        return t('weAreUnableToFindAReservation', 'We are unable to find a reservation')
      case WorkInstructionWarningReason.StuffingAndContainerCriticallyDamaged:
        return t('containerIsCriticallyDamaged', 'Container is critically damaged')
      case WorkInstructionWarningReason.TallyNotConfirmed:
        return t('theTallyClerkHasntConfirmed', `The tally clerk hasn't confirmed`)
      case WorkInstructionWarningReason.FutureOutbound:
        return t('futureLoad', 'Future load')
      case WorkInstructionWarningReason.RequiresPlugOut:
        return t('unplugWarning', 'Careful: reefer unplug has not been confirmed')
      case WorkInstructionWarningReason.LoadingApprovalMissing:
        return t('loadingApprovalMissing', 'Loading approval is missing.')
      case WorkInstructionWarningReason.DischargeApprovalMissing:
        return t('dischargeApprovalMissing', 'Discharge approval is missing.')
      case WorkInstructionWarningReason.WorkInstructionStopped:
        return t('workInstructionWasStopped', 'Work instruction was stopped')
      case WorkInstructionWarningReason.ExpiredRelease:
        return t('releaseHasExpired', 'Release has expired.')
      default:
        return t('somethingWentWrong', 'Something went wrong')
    }
  }

  const hasContactControlRoomMessage = () => {
    switch (warningReason) {
      case WorkInstructionWarningReason.LinkedInboundNotFinished:
      case WorkInstructionWarningReason.LinkedInboundForceFinishedByGateOutWithoutLocation:
      case WorkInstructionWarningReason.OnYardWithoutPosition:
      case WorkInstructionWarningReason.RequiresReservation:
      case WorkInstructionWarningReason.StuffingAndContainerCriticallyDamaged:
      case WorkInstructionWarningReason.LoadingApprovalMissing:
      case WorkInstructionWarningReason.DischargeApprovalMissing:
        return true
      case WorkInstructionWarningReason.CarrierNotReadyForOperation:
      case WorkInstructionWarningReason.TallyNotConfirmed:
      case WorkInstructionWarningReason.FutureOutbound:
      default:
        return false
    }
  }

  const hasUpdatePositionMessage = () => {
    switch (warningReason) {
      case WorkInstructionWarningReason.WorkInstructionStopped:
        return isOnStepper ? true : false
      default:
        return false
    }
  }

  const holdWarningNames = getHoldWarningNames(
    operationType,
    entryHoldRestriction,
    exitHoldRestriction,
    yardHoldRestriction,
  )

  return (
    <>
      {warningReason && !nonAlertableWarningReasons.includes(warningReason) && (
        <Alert severity='warning'>
          <AlertTitle>{getTitle()}</AlertTitle>
          {hasContactControlRoomMessage() && (
            <Typography variant='body2'>
              {t(
                'contactControlRoomForInformation',
                'Contact the control room to get further information',
              )}
            </Typography>
          )}

          {hasUpdatePositionMessage() && (
            <Typography variant='body2'>
              {t('pleaseUpdateThePosition', 'Please update the position')}
            </Typography>
          )}
        </Alert>
      )}

      {holdWarningNames.length > 0 && (
        <Alert severity='warning'>
          <AlertTitle>
            {t(
              'containerHasActiveHolds',
              'This container has active holds: {holdTypes}. You are allowed to proceed.',
              { holdTypes: holdWarningNames.join(', ') },
            )}
          </AlertTitle>
        </Alert>
      )}
    </>
  )
}
